import type {
  PopulateTargetingValuesInput,
  PopulateTargetingValuesQuery,
  TargetingTermValue,
} from '../../../../../../apis/graphql';
import { PRESET_ID, PRESET_SUB_CLASS } from '../../../../../TargetingDropdowns/constants';
import type { Term, ValueSet } from '../../targetingCell.types';
import type { CellChanges } from '../DefaultCellItems';

export type TargetingTermDisplayNameLookup = Record<string, Record<string, string>>;

export const isTermValueProvidedByChangelog = (
  targetingTermValues: Array<TargetingTermValue> | null | undefined,
  dimension: string,
  valueKey: string
): boolean => {
  if (!targetingTermValues?.length) {
    return false;
  }

  return targetingTermValues.some(
    (termValue) => termValue.dimension?.id === dimension && termValue.value?.id === valueKey
  );
};

export const buildTargetingTermDisplayNameLookup = (
  targetingTermValues: Array<TargetingTermValue> | null | undefined
): TargetingTermDisplayNameLookup => {
  if (!targetingTermValues?.length) {
    return {};
  }

  return targetingTermValues.reduce<TargetingTermDisplayNameLookup>((lookup, termValue) => {
    const displayName = termValue.value?.displayName;
    const dimensionId = termValue.dimension?.id;
    const valueId = termValue.value?.id;

    if (!displayName || !dimensionId || !valueId) {
      return lookup;
    }

    if (!lookup[dimensionId]) {
      lookup[dimensionId] = {};
    }

    lookup[dimensionId][valueId] = displayName;
    return lookup;
  }, {});
};

export const getDisplayNameFromLookup = (
  lookup: TargetingTermDisplayNameLookup | null | undefined,
  dimension: string,
  valueId: string
): string | undefined => lookup?.[dimension]?.[valueId];

export const applyTargetingTermDisplayNamesToTerms = (
  termsList: Term[],
  lookup: TargetingTermDisplayNameLookup | null | undefined
): Term[] => {
  if (!lookup || !Object.keys(lookup).length) {
    return termsList;
  }

  return termsList.map((term) => {
    const dimension = term.dimension;
    if (!dimension) {
      return term;
    }

    const valueSet = getTermValueSet(term);
    const eachValueIsString = valueSet.every((value) => typeof value === 'string');
    if (!eachValueIsString) {
      return term;
    }

    const preparedValues = valueSet
      .filter((value): value is string => typeof value === 'string')
      .map((valueId) => {
        const displayName = getDisplayNameFromLookup(lookup, dimension, valueId);

        return {
          id: valueId,
          name: displayName ?? '',
        };
      });

    return {
      ...term,
      preparedValues,
    };
  });
};

export const isPresetTargetingTerm = (term: Term): boolean => term.subClass === PRESET_SUB_CLASS;

const toValidTerm = (term: Term): Term | null => {
  if (term.dimension) {
    return { ...term };
  }

  if (isPresetTargetingTerm(term) && term.id) {
    return {
      dimension: PRESET_ID,
      valueSet: [term.id],
      not: term.not,
    };
  }

  return null;
};

export const getValidTermsArray = (terms: Term[] | Term): Term[] => {
  const termList = Array.isArray(terms) ? terms : [terms];

  return termList.reduce((terms: Term[], term: Term) => {
    const valid = toValidTerm(term);

    return valid ? [...terms, valid] : terms;
  }, []);
};

export const getTermListFromCellChanges = (changes: CellChanges[] | CellChanges): Term[] => {
  const terms: Term[] = [];
  if (Array.isArray(changes)) {
    changes.forEach((change) => {
      terms.push(...getValidTermsArray(change.payload as Term[] | Term));
    });
  } else {
    terms.push(...getValidTermsArray(changes.payload as Term[] | Term));
  }

  return terms;
};

export const getTermValueSet = (term: Term): (ValueSet | string)[] => {
  return term.valueSet || term['value-set'] || [];
};

export const getTargetingValuesToPopulateAndSetTermPreparedValues = (
  termsList: Term[],
  targetingTermValues: Array<TargetingTermValue> | null | undefined = null
): PopulateTargetingValuesInput[] => {
  const targetingsToPopulate: PopulateTargetingValuesInput[] = [];
  termsList.forEach((term) => {
    if (term?.dimension) {
      const termTargetingsToPopulate: PopulateTargetingValuesInput = {
        dimension: term.dimension,
        valueSet: [],
      };

      const valueSet = getTermValueSet(term);

      const eachValueIsString = valueSet.every((value) => typeof value === 'string');
      if (eachValueIsString) {
        if (!term.preparedValues) {
          term.preparedValues = [];
        }
        valueSet.forEach((value) => {
          if (typeof value === 'string') {
            const existingPreparedValue = term.preparedValues?.find((preparedValue) => preparedValue.id === value);
            const isProvidedByChangelog = isTermValueProvidedByChangelog(targetingTermValues, term.dimension!, value);
            const hasResolvedDisplayName =
              isProvidedByChangelog ||
              (!!existingPreparedValue?.name && !existingPreparedValue.name.endsWith('_NOT_FOUND'));

            if (!hasResolvedDisplayName) {
              termTargetingsToPopulate.valueSet.push(value);
              if (!existingPreparedValue) {
                term.preparedValues?.push({
                  id: value,
                  name: '',
                });
              }
            }
          }
        });
        if (termTargetingsToPopulate.valueSet.length) {
          targetingsToPopulate.push(termTargetingsToPopulate);
        }
      }
    }
  });

  return targetingsToPopulate;
};

export const getValueToShowInCaseDisplayNameWasNotFound = (dimension: string, value: string): string => {
  return dimension !== 'age' ? `${value}_NOT_FOUND` : value;
};

export const buildPresetLabel = (displayName: string, id: string): string => `${displayName} (${id})`;

export const prepareTermsForUi = (
  termsList: Term[],
  termsWithValues: PopulateTargetingValuesQuery['populateTargetingValues']
): Term[] => {
  const simplifiedTermsWithValues: Record<string, Record<string, string>> = {};

  termsWithValues.forEach((termsWithValues) => {
    if (!simplifiedTermsWithValues[termsWithValues.dimension]) {
      simplifiedTermsWithValues[termsWithValues.dimension] = {};
    }
    termsWithValues.valueSet.forEach((valueSetItem) => {
      simplifiedTermsWithValues[termsWithValues.dimension][valueSetItem.id] = valueSetItem.displayName;
    });
  });

  return termsList.map((term) => {
    const newTerm: Term = {
      ...term,
      preparedValues: term.preparedValues?.map((preparedValue) => {
        const displayName =
          term.dimension &&
          simplifiedTermsWithValues[term.dimension] &&
          simplifiedTermsWithValues[term.dimension][preparedValue.id]
            ? simplifiedTermsWithValues[term.dimension][preparedValue.id]
            : getValueToShowInCaseDisplayNameWasNotFound(term.dimension || '', preparedValue.id);

        return {
          id: preparedValue.id,
          name: displayName,
        };
      }),
    };

    return newTerm;
  });
};
